import React, { useRef, useEffect, useState } from 'react';
import { useFontLoader } from '../../../../dummies/RichTextEditor/useFontLoader';

const Board = ({ data = { notices: [] }, title = 'לוח מודעות', showTitle = true }) => {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const originalContentRef = useRef(null);
  const firstSetRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const activeNotices = (data.notices || []).filter(
    n => !n.expiryDate || new Date(n.expiryDate) > now
  );

  const hasNotices = activeNotices.length > 0;

  // טעינת פונטים מכל המודעות
  const allNoticesHtml = activeNotices.map(n => n.content).join(' ') || '';
  useFontLoader(allNoticesHtml);

  useEffect(() => {
    const checkScroll = () => {
      if (!containerRef.current || !firstSetRef.current || !hasNotices) return;
      const containerHeight = containerRef.current.clientHeight;
      const contentHeight = firstSetRef.current.offsetHeight;
      setNeedsScroll(containerHeight > 0 && contentHeight > containerHeight + 2);
    };

    // מחכים שה-DOM יתעדכן לפני הבדיקה
    const timer = setTimeout(checkScroll, 100);

    const resizeObserver = new ResizeObserver(checkScroll);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [activeNotices, hasNotices]);

  const renderContent = () => {
    if (!hasNotices) {
      return <p style={{ color: '#795548', fontSize: '0.875em' }}>אין מודעות להצגה</p>;
    }

    return (
      <div ref={containerRef} style={{
        overflow: 'hidden',
        flex: 1,
        position: 'relative'
      }}>
        {needsScroll && (
          <style>
            {`
              @keyframes scrollNotices {
                0% { transform: translateY(0); }
                100% { transform: translateY(calc(-100% / 3)); }
              }
            `}
          </style>
        )}
        <div ref={contentRef} style={{
          animation: needsScroll ? 'scrollNotices 30s linear infinite' : 'none'
        }}>
          <div ref={originalContentRef} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <ul ref={firstSetRef} style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {activeNotices.map((notice) => (
                <li key={notice.id} data-notice-item style={{
                  display: 'flex',
                  gap: '0.75em',
                  alignItems: 'start',
                  padding: '0.75em',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: '1.25em',
                  border: '0.03125em solid #EFEBE9'
                }}>
                  <span style={{ color: '#D4AF37', fontSize: '0.875em', lineHeight: '1', marginTop: '0.25em' }}>❖</span>
                  <p style={{
                    margin: 0,
                    fontSize: '0.75em',
                    color: '#5D4037'
                  }} dangerouslySetInnerHTML={{ __html: notice.content }} />
                </li>
              ))}
            </ul>
            {needsScroll && (
              <>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {activeNotices.map((notice) => (
                    <li key={`dup1-${notice.id}`} style={{
                      display: 'flex',
                      gap: '0.75em',
                      alignItems: 'start',
                      padding: '0.75em',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      borderRadius: '1.25em',
                      border: '0.03125em solid #EFEBE9'
                    }}>
                      <span style={{ color: '#D4AF37', fontSize: '0.875em', lineHeight: '1', marginTop: '0.25em' }}>❖</span>
                      <p style={{
                        margin: 0,
                        fontSize: '0.75em',
                        color: '#5D4037'
                      }} dangerouslySetInnerHTML={{ __html: notice.content }} />
                    </li>
                  ))}
                </ul>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {activeNotices.map((notice) => (
                    <li key={`dup2-${notice.id}`} style={{
                      display: 'flex',
                      gap: '0.75em',
                      alignItems: 'start',
                      padding: '0.75em',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      borderRadius: '1.25em',
                      border: '0.03125em solid #EFEBE9'
                    }}>
                      <span style={{ color: '#D4AF37', fontSize: '0.875em', lineHeight: '1', marginTop: '0.25em' }}>❖</span>
                      <p style={{
                        margin: 0,
                        fontSize: '0.75em',
                        color: '#5D4037'
                      }} dangerouslySetInnerHTML={{ __html: notice.content }} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      padding: '20px',
      direction: 'rtl',
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      fontSize: 'inherit' // יורש מה-WidgetWrapper
    }}>
      {/* Header */}
      {showTitle && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '0.0625em solid #D7CCC8',
          paddingBottom: '8px',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="material-symbols-outlined" style={{ color: '#8D6E63' }}>campaign</span>
            <h3 style={{
              margin: 0,
              fontWeight: 'bold',
              color: '#3E2723',
              fontSize: '0.875em'
            }}>{title}</h3>
          </div>
        </div>
      )}

      {/* Content List */}
      {renderContent()}
    </div>
  );
};

export default Board;

